import { useState, useCallback, createContext, useContext, type ReactNode } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (type: ToastType, title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast debe usarse dentro de ToastProvider');
  return ctx;
}

const iconMap: Record<ToastType, string> = {
  success: 'ri-checkbox-circle-fill',
  error: 'ri-close-circle-fill',
  warning: 'ri-error-warning-fill',
  info: 'ri-information-fill',
};

const bgMap: Record<ToastType, string> = {
  success: 'border-emerald-400',
  error: 'border-red-400',
  warning: 'border-amber-400',
  info: 'border-sky-400',
};

const iconColorMap: Record<ToastType, string> = {
  success: 'text-emerald-500',
  error: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-sky-500',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((type: ToastType, title: string, message?: string) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((prev) => [...prev, { id, type, title, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast }}>
      {children}
      <div className="fixed top-4 right-4 z-[80] flex flex-col gap-2 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`pointer-events-auto flex items-start gap-3 bg-background-50 border-l-4 rounded-lg shadow-lg px-4 py-3 min-w-[300px] max-w-[420px] animate-[slideInRight_250ms_ease-out] ${bgMap[toast.type]}`}
          >
            <i className={`${iconMap[toast.type]} text-lg ${iconColorMap[toast.type]} mt-0.5 flex-shrink-0`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground-900">{toast.title}</p>
              {toast.message && (
                <p className="text-xs text-foreground-500 mt-0.5">{toast.message}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}